/**
 * Single form-resolution rule for App mode (spec 2026-07-12):
 *   custom form from designerTree (isDefaultForm → the only one → by name)
 *     → otherwise the generated default from GET /form/{kind}.
 * Core has no module-specific fallbacks here by design.
 */
import apiClient from '@/utils/api'
import type { FormSchema } from '@/designer/types/form.types'
import type { ConfigNode } from '@/designer/types/config.types'
import type { AttributeDef } from '@/designer/services/configObjectApi'
import { isEntityNode } from '@/designer/utils/entityMetadataKey'
import { fetchDesignerTree } from './designerTree'
import { stripAppHiddenFormFields } from './stripAppHiddenFormFields'

export type FormSource = 'custom' | 'generated'

export interface ResolvedForm {
  schema: FormSchema
  source: FormSource
  /** True when a custom form existed but was broken and we fell back to generated. */
  degraded?: boolean
  /** Present when source==='generated': fresh attributes from the API. */
  attributes?: AttributeDef[]
}

type FormEntry = { name: string; schema: FormSchema; isDefault: boolean }

function findEntityNode(nodes: ConfigNode[], objectName: string): ConfigNode | null {
  for (const node of nodes) {
    if (isEntityNode(node) && node.name === objectName) return node
    if (node.children?.length) {
      const found = findEntityNode(node.children, objectName)
      if (found) return found
    }
  }
  return null
}

function collectForms(entity: ConfigNode, kind: 'item' | 'list'): FormEntry[] {
  const out: FormEntry[] = []
  for (const ch of entity.children ?? []) {
    if (ch.type !== 'objectForms') continue
    for (const formNode of ch.children ?? []) {
      if (formNode.type !== 'objectForm') continue
      const fs = (formNode.formSchema ?? (formNode as { schema?: FormSchema }).schema) as
        | FormSchema
        | undefined
      if (!fs) continue
      const fsKind = fs.kind || 'item'
      if (fsKind !== kind) continue
      out.push({
        name: (fs.name || formNode.name || '').trim(),
        schema: fs,
        isDefault: !!formNode.isDefaultForm,
      })
    }
  }
  return out
}

function pickDefaultListForm(forms: FormEntry[]): FormEntry | null {
  if (!forms.length) return null
  return (
    forms.find((f) => f.isDefault) ??
    (forms.length === 1 ? forms[0] : forms.find((f) => f.name === 'ListForm') ?? forms[0])
  )
}

function pickDefaultItemForm(forms: FormEntry[]): FormEntry | null {
  if (!forms.length) return null
  const marked = forms.find((f) => f.isDefault)
  if (marked) return marked
  if (forms.length === 1) return forms[0]
  const nonGeneric = forms.filter((f) => f.name !== 'ItemForm')
  return nonGeneric.length === 1 ? nonGeneric[0] : forms[0]
}

function isValid(schema: FormSchema | undefined | null): schema is FormSchema {
  return !!schema && Array.isArray(schema.root) && schema.root.length > 0
}

async function fetchGenerated(
  connectionId: string,
  objectName: string,
  kind: 'item' | 'list',
  formName?: string,
): Promise<ResolvedForm | null> {
  try {
    const qs = formName ? `?form_name=${encodeURIComponent(formName)}` : ''
    const resp = await apiClient.get(
      `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/form/${kind}${qs}`,
    )
    const body = resp.data as {
      source?: string
      schema?: FormSchema
      attributes?: AttributeDef[]
    }
    if (!isValid(body?.schema)) return null
    const source: FormSource = body.source === 'generated' ? 'generated' : 'custom'
    return { schema: body.schema, source, attributes: body.attributes }
  } catch {
    return null
  }
}

export async function resolveFormSchema(
  connectionId: string,
  objectName: string,
  kind: 'item' | 'list',
  formName?: string,
): Promise<ResolvedForm | null> {
  let sawBrokenCustom = false
  try {
    const tree = await fetchDesignerTree(connectionId)
    const entity = tree.length ? findEntityNode(tree, objectName) : null
    if (entity) {
      const forms = collectForms(entity, kind)
      const want = (formName || '').trim()
      const hit = want
        ? forms.find((f) => f.name === want) ?? null
        : kind === 'list'
          ? pickDefaultListForm(forms)
          : pickDefaultItemForm(forms)
      if (hit) {
        if (isValid(hit.schema)) return { schema: stripAppHiddenFormFields(hit.schema), source: 'custom' }
        sawBrokenCustom = true
      }
    }
  } catch {
    // tree unavailable → fall through to the API
  }
  const generated = await fetchGenerated(connectionId, objectName, kind, formName)
  if (!generated) return null
  const stripped = { ...generated, schema: stripAppHiddenFormFields(generated.schema) }
  return sawBrokenCustom ? { ...stripped, degraded: true } : stripped
}

export const __testables = { pickDefaultListForm, pickDefaultItemForm, collectForms }
